import React, { useState, createContext, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import funcionalidades from "./AuthContext.funcionalidades";
import { history } from "../history";
import { selectInputCacheActions } from "../redux/selectInputCache";
import { apiRoutes } from "../hooks/apiClient";

const AuthContext = createContext({
  isAuthenticated: false,
  unreadNotifications: [],
  login: () => {},
});

const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState({});
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const dispatch = useDispatch();

  // Tentar carregar dados de login da sessão
  useEffect(() => {
    const userJson = localStorage.getItem("user");

    if (userJson) {
      const parsedUser = JSON.parse(userJson);
      setUser(parsedUser);
      setPermissions(parsedUser.permissoes); 
    }
  }, []);

  const handleLogin = useCallback(
    (signinData) => {
      localStorage.setItem("authToken", JSON.stringify(signinData.result.token));
      localStorage.setItem("user", JSON.stringify(signinData.result.user));

      setUser(signinData.result.user);
      setAuthenticated(true);
      setPermissions(signinData.result.user.permissoes);
      dispatch(selectInputCacheActions.reset());
      window.location.href = "/app/home";
    },
    [setUser, setAuthenticated, setPermissions, dispatch],
  );

  const handleLogout = useCallback(() => {
    setAuthenticated(false);
    localStorage.clear();
    dispatch(selectInputCacheActions.reset());
    history.push("/");

    fetch(apiRoutes.auth.autenticacao.signOut.url, { credentials: "include" });
  }, [setAuthenticated, dispatch]);

  const isAllowed = useCallback(
    (permissionRequired) => {
      try {
        let currentPermissions = permissions;

        if (!currentPermissions || !currentPermissions.length) {
          const localStorageUser = JSON.parse(localStorage.getItem("user"));
          currentPermissions = localStorageUser?.permissoes || [];
        }

        return currentPermissions.filter((x) => x.nomeFuncionalidade.includes(permissionRequired)).length >= 1;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    [permissions],
  );

  const recuperarSenhaSigat = useCallback(async () => {
    try {
      const response = await fetch(apiRoutes.auth.autenticacao.sessionPasswords.url, {
        method: apiRoutes.auth.autenticacao.sessionPasswords.method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.result || "Erro ao recuperar senha do SIGAT");
      }

      const data = await response.json();
      return data.result?.ora || data.ora || "";
    } catch (error) {
      console.error("Erro ao recuperar senha:", error);
      toast.error(error.message || "Não foi possível recuperar a senha do SIGAT");
      throw error;
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      isAuthenticated: authenticated,
      user,
      unreadNotifications,
      setUnreadNotifications,
      isAllowed,
      handleLogin,
      handleLogout,
      recuperarSenhaSigat,
    }),
    [authenticated, handleLogin, handleLogout, isAllowed, unreadNotifications, user, recuperarSenhaSigat],
  );
  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider, funcionalidades };
